import { createContext, useContext, useMemo, type FC } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { GridColumn } from './types'
import GridRow, { type GridRowProps } from './GridRow'
import classes from './grid-theme.module.scss'
import useTranslation from '@/hooks/useTranslation'

interface RowDragHandleValue {
  attributes: Record<string, unknown>
  listeners: Record<string, unknown> | undefined
  disabled: boolean
}

/**
 * The drag listeners belong to the row (dnd-kit's sortable node) but the handle
 * is painted by a cell several levels down, so they travel by context rather
 * than through every renderer in between.
 */
const RowDragContext = createContext<RowDragHandleValue | null>(null)

export const ROW_DRAG_FIELD = '__rowDrag'

const GripGlyph: FC = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
    <g fill="currentColor">
      <circle cx="2.5" cy="2.5" r="1.3" />
      <circle cx="7.5" cy="2.5" r="1.3" />
      <circle cx="2.5" cy="7" r="1.3" />
      <circle cx="7.5" cy="7" r="1.3" />
      <circle cx="2.5" cy="11.5" r="1.3" />
      <circle cx="7.5" cy="11.5" r="1.3" />
    </g>
  </svg>
)

const RowDragHandle: FC = () => {
  const { t } = useTranslation()
  const value = useContext(RowDragContext)
  if (!value || value.disabled) return null

  return (
    <span
      data-no-cell-edit
      className={classes.rowDragHandle}
      role="button"
      tabIndex={-1}
      aria-label={t('gridReorderRow')}
      {...value.attributes}
      {...value.listeners}
    >
      <GripGlyph />
    </span>
  )
}

/** The leading handle column the grid inserts when row drag is on. */
export const rowDragColumn = <T,>(): GridColumn<T> => ({
  field: ROW_DRAG_FIELD,
  headerName: '',
  width: 34,
  minWidth: 34,
  maxWidth: 34,
  pinned: 'left',
  sortable: false,
  resizable: false,
  editable: false,
  lockPosition: true,
  cellRenderer: RowDragHandle,
})

interface SortableGridRowProps<T> extends GridRowProps<T> {
  canDrag: boolean
}

/**
 * A grid row that can be picked up.
 *
 * The transform is applied to the row's own element rather than a drag preview
 * so the row keeps its cells — a virtualised row already carries an absolute
 * `translateY`, and dnd-kit's transform has to compose with it.
 */
const SortableGridRow = <T,>({ canDrag, ...rowProps }: SortableGridRowProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rowProps.rowId, disabled: !canDrag })

  const value = useMemo<RowDragHandleValue>(
    () => ({
      attributes: attributes as unknown as Record<string, unknown>,
      listeners: listeners as unknown as Record<string, unknown> | undefined,
      disabled: !canDrag,
    }),
    [attributes, listeners, canDrag],
  )

  const dragTransform = CSS.Transform.toString(transform)
  const baseTransform = rowProps.style?.transform

  return (
    <RowDragContext.Provider value={value}>
      <GridRow
        {...rowProps}
        measureRef={(el: HTMLElement | null) => {
          setNodeRef(el as HTMLElement)
          rowProps.measureRef?.(el)
        }}
        style={{
          ...rowProps.style,
          transform: [baseTransform, dragTransform].filter(Boolean).join(' '),
          transition,
          ...(isDragging ? { zIndex: 5, position: 'relative' } : null),
        }}
        rowClassName={[rowProps.rowClassName, isDragging ? classes.rowDragging : '']
          .filter(Boolean)
          .join(' ')}
      />
    </RowDragContext.Provider>
  )
}

export default SortableGridRow
